import dotPropImmutable from 'dot-prop-immutable';
import {
  ProjectTemplateData,
  ProjectTemplateTaskData,
} from 'data/templates/projects';
import { insert } from 'data/templates/projects/utils';
import {
  TemplateData,
  TemplateDataHashMap,
  TemplateTaskData,
  TemplatePhaseData,
  PhaseAsParent,
  TaskAsParent,
} from './types';

const toTasksHashMap = (
  tasks: ProjectTemplateTaskData[],
): Record<number, ProjectTemplateTaskData> =>
  tasks.reduce(
    (resultTasks, task) => ({
      [task.id]: task,
      ...((task.children ?? []).length > 0
        ? toTasksHashMap(task.children ?? [])
        : {}),
      ...resultTasks,
    }),
    {},
  );

export const toHashMap = (data: ProjectTemplateData[]): TemplateDataHashMap =>
  (data ?? []).reduce(
    (result, projectPhase) => ({
      projectPhases: {
        ...result.projectPhases,
        [projectPhase.id]: projectPhase,
      },
      tasks: {
        ...result.tasks,
        ...toTasksHashMap(projectPhase.children ?? []),
      },
    }),
    { projectPhases: {}, tasks: {} },
  );

const getChildTasks = (
  tasks: number[],
  tasksHashMap: Record<number, TemplateTaskData>,
  level,
  isParentPrivate: boolean,
): TemplateTaskData[] => {
  return tasks
    .map((taskId) => {
      const task = tasksHashMap[taskId];

      if (task == null) {
        return null;
      }

      return {
        ...task,
        level,
        className: `level-${level} ${
          task.private || isParentPrivate ? 'private' : ''
        }`,
        children: getChildTasks(
          task.data.subTasks,
          tasksHashMap,
          level + 1,
          isParentPrivate || task.private,
        ),
      };
    })
    .filter(Boolean) as TemplateTaskData[];
};

export const toTreeData = ({
  projectPhases,
  tasks,
}: TemplateDataHashMap): TemplateData[] =>
  Object.keys(projectPhases)
    .map((projectPhaseId) => {
      const projectPhase = projectPhases[projectPhaseId] as TemplatePhaseData;

      return {
        ...projectPhase,
        level: 0,
        className: `project-phase ${projectPhase.private ? 'private' : ''}`,
        children: getChildTasks(
          projectPhase.data.tasks,
          tasks,
          1,
          projectPhase.private,
        ),
      };
    })
    .filter(Boolean);

export const removeTaskFromPhase = (
  data: TemplateDataHashMap,
  projectPhaseId: number,
  taskId: number,
): TemplateDataHashMap => {
  const tasksPath = `projectPhases.${projectPhaseId}.data.tasks`;
  const phaseTasks = dotPropImmutable.get(data, tasksPath, []);

  return dotPropImmutable.set(
    data,
    tasksPath,
    phaseTasks.filter((id) => id !== taskId),
  );
};

export const removeSubTaskFromTask = (
  data: TemplateDataHashMap,
  parentTaskId: number,
  taskId: number,
): TemplateDataHashMap => {
  const tasksPath = `tasks.${parentTaskId}.data.subTasks`;
  const taskSubTasks = dotPropImmutable.get(data, tasksPath, []);

  return dotPropImmutable.set(
    data,
    tasksPath,
    taskSubTasks.filter((id) => id !== taskId),
  );
};

export const removeTaskFromParent = (
  data: TemplateDataHashMap,
  task: TemplateTaskData,
): TemplateDataHashMap => {
  if (task.data.projectPhase) {
    return removeTaskFromPhase(
      data,
      task.data.projectPhase.projectPhaseId,
      task.id,
    );
  }

  return removeSubTaskFromTask(data, task.data.parentTask?.taskId, task.id);
};

export const insertTaskIntoPhase = (
  data: TemplateDataHashMap,
  projectPhaseId: number,
  taskId: number,
  insertAt: number,
): TemplateDataHashMap => {
  const path = `projectPhases.${projectPhaseId}.data.tasks`;
  const tasks = dotPropImmutable.get(data, path, []);

  let updated = dotPropImmutable.set(
    data,
    path,
    insert(tasks, insertAt, taskId),
  );

  updated = dotPropImmutable.set(updated, `tasks.${taskId}.data.projectPhase`, {
    projectPhaseId,
  });

  return dotPropImmutable.set(
    updated,
    `tasks.${taskId}.data.parentTask`,
    undefined,
  );
};

export const insertSubTaskIntoTask = (
  data: TemplateDataHashMap,
  parentTaskId: number,
  taskId: number,
  insertAt: number,
): TemplateDataHashMap => {
  const path = `tasks.${parentTaskId}.data.subTasks`;

  const subTasks = dotPropImmutable.get(data, path, []);

  let updated = dotPropImmutable.set(
    data,
    path,
    insert(subTasks, insertAt, taskId),
  );

  updated = dotPropImmutable.set(
    updated,
    `tasks.${taskId}.data.projectPhase`,
    undefined,
  );

  return dotPropImmutable.set(updated, `tasks.${taskId}.data.parentTask`, {
    taskId: parentTaskId,
  });
};
export const insertTaskIntoParent = (
  data: TemplateDataHashMap,
  newParent: Pick<TemplateData, 'id' | 'type' | 'private'>,
  taskId: number,
  insertAt: number,
): {
  data: TemplateDataHashMap;
  parent: PhaseAsParent | TaskAsParent;
} => {
  if (newParent.type === 'PROJECT_PHASE') {
    return {
      data: insertTaskIntoPhase(data, newParent.id, taskId, insertAt),
      parent: { projectPhase: { projectPhaseId: newParent.id } },
    };
  }

  return {
    data: insertSubTaskIntoTask(data, newParent.id, taskId, insertAt),
    parent: { parentTask: { taskId: newParent.id } },
  };
};
export const getParent = (
  data: TemplateDataHashMap,
  task: TemplateTaskData,
): TemplateData => {
  if (task.data.projectPhase) {
    return dotPropImmutable.get(
      data,
      `projectPhases.${task.data.projectPhase.projectPhaseId}`,
      {},
    );
  }

  return dotPropImmutable.get(
    data,
    `tasks.${task.data.parentTask?.taskId}`,
    {},
  );
};

export const getSibilings = (
  data: TemplateDataHashMap,
  task: TemplateTaskData,
): number[] => {
  if (task.data.projectPhase) {
    return dotPropImmutable.get(
      data,
      `projectPhases.${task.data.projectPhase.projectPhaseId}.data.tasks`,
      [],
    );
  }

  return dotPropImmutable.get(
    data,
    `tasks.${task.data.parentTask?.taskId}.data.subTasks`,
    [],
  );
};

export const getDepthOfTask = (
  data: TemplateDataHashMap,
  taskId: number,
): number => {
  const subTasks: number[] = dotPropImmutable.get(
    data,
    `tasks.${taskId}.data.subTasks`,
    [],
  );

  if (subTasks.length === 0) {
    return 1;
  }

  return (
    1 +
    subTasks.reduce((maxLevelDeep, subTaskId) => {
      const levelDeep = getDepthOfTask(data, subTaskId);

      return levelDeep > maxLevelDeep ? levelDeep : maxLevelDeep;
    }, 0)
  );
};
